/**
 * @file Title screen.
 *
 * Nothing simulates here: the scene is empty, the camera is parked at the
 * origin and the state only waits for a confirm input. Esc is the menu key
 * hierarchy root — arriving here tears down any active level.
 */

import * as THREE from 'three';
import { GameState } from './GameState.js';
import { GameStates } from './StateMachine.js';
import { Action } from '../../input/InputManager.js';
import { Events } from '../EventBus.js';
import { prepareNewRun, teardownLevel } from '../../world/Level.js';

export class MenuState extends GameState {
  /**
   * @param {import('../Context.js').GameContext} ctx Shared game context.
   * @param {import('./StateMachine.js').StateMachine} machine Owning machine.
   */
  constructor(ctx, machine) {
    super(ctx, machine);
    /** @type {THREE.Vector3} @private */
    this._origin = new THREE.Vector3(0, 0, 0);
    /** @type {(() => void) | null} @private */
    this._unsubscribeNav = null;
  }

  /** @returns {void} */
  enter() {
    // Quit-to-menu from pause/game-over must free the dungeon.
    teardownLevel(this.ctx);
    this.ctx.cameraController.snapTo(this._origin);
    // The nav list's PLAY row answers through this rather than the state
    // reaching into the HUD, same contract as the pause popup's buttons.
    this._unsubscribeNav = this.ctx.events.on(Events.MENU_NAV_ACTION, ({ action }) => {
      if (action === 'play') this._start();
    });
  }

  /** @returns {void} */
  exit() {
    this._unsubscribeNav?.();
    this._unsubscribeNav = null;
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const { input } = this.ctx;
    if (input.justPressed(Action.CONFIRM) || input.justPressed(Action.FIRE)) this._start();
  }

  /** @returns {void} @private */
  _start() {
    prepareNewRun(this.ctx);
    this.machine.change(GameStates.LOADING);
  }
}
